import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeSanitize, { defaultSchema } from 'rehype-sanitize';

const customSchema = {
  ...defaultSchema,
  attributes: {
    ...defaultSchema.attributes,
    '*': [
      ...(defaultSchema.attributes['*'] || []),
      'className',
      'style',
    ],
  },
};

const BUSINESS_ANALYSIS_TENANTS = new Set([
  '01c34123-e72b-4b74-849b-865c6fc2672e',
  '2ab5560a-591e-49c4-b21c-728422144237',
]);

const RiskScoresTable = ({ riskScores }) => {
  if (!riskScores || Object.keys(riskScores).length === 0) {
    return null;
  }

  // Filter out non-numeric scores
  const numericScores = Object.entries(riskScores).filter(([_, score]) => typeof score === 'number');

  if (numericScores.length === 0) {
    return null;
  }

  return (
    <div className="my-6 overflow-x-auto">
      <table className="min-w-full border border-gray-300 rounded-lg">
        <thead>
          <tr className="bg-gray-50">
            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 border-b border-gray-300">
              Risk Category
            </th>
            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 border-b border-gray-300">
              Score
            </th>
          </tr>
        </thead>
        <tbody>
          {numericScores.map(([category, score], index) => (
            <tr key={category} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
              <td className="px-4 py-3 text-sm text-gray-900 border-b border-gray-200 font-medium">
                {category}
              </td>
              <td className="px-4 py-3 text-sm text-gray-700 border-b border-gray-200">
                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                  score >= 7 ? 'bg-red-100 text-red-800' :
                  score >= 5 ? 'bg-yellow-100 text-yellow-800' :
                  'bg-green-100 text-green-800'
                }`}>
                  {score}/10
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const BusinessAnalysisSection = ({ businessAnalysis }) => {
  if (!businessAnalysis || typeof businessAnalysis !== 'object') return null;

  const domain = businessAnalysis.domain_of_expertise;
  const quality = businessAnalysis.quality_of_service;
  const responsiveness = businessAnalysis.responsiveness;
  const pricing = businessAnalysis.pricing_level;

  const domains = domain?.domains;
  const hasAny =
    domain || quality || responsiveness || pricing;

  if (!hasAny) return null;

  return (
    <div className="mt-8 not-prose">
      <h3 className="text-lg font-bold mb-4">Business Analysis</h3>

      {domain && (
        <div className="mb-6 p-4 bg-white rounded-lg border border-gray-200">
          <h4 className="text-md font-semibold text-gray-900 mb-2">Domain of Expertise</h4>
          {domain.summary && (
            <p className="text-sm text-gray-700 mb-4 leading-relaxed">{domain.summary}</p>
          )}

          {Array.isArray(domains) && domains.length > 0 && (
            <div className="space-y-4">
              {domains.map((d, idx) => (
                <div key={`${d?.name || 'domain'}-${idx}`} className="p-4 rounded-md bg-gray-50 border border-gray-200">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-gray-900">{d?.name || 'Unnamed domain'}</div>
                      {d?.description && (
                        <div className="text-sm text-gray-700 mt-1 leading-relaxed">{d.description}</div>
                      )}
                    </div>
                    {typeof d?.score === 'number' && (
                      <span
                        className={`shrink-0 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          d.score >= 8 ? 'bg-green-100 text-green-800' :
                          d.score >= 6 ? 'bg-yellow-100 text-yellow-800' :
                          'bg-red-100 text-red-800'
                        }`}
                      >
                        {d.score}/10
                      </span>
                    )}
                  </div>

                  {Array.isArray(d?.sub_categories) && d.sub_categories.length > 0 && (
                    <div className="mt-3 overflow-x-auto">
                      <table className="min-w-full border border-gray-200 rounded-md bg-white">
                        <thead>
                          <tr className="bg-gray-100">
                            <th className="px-3 py-2 text-left text-xs font-semibold text-gray-800 border-b border-gray-200">Sub-category</th>
                            <th className="px-3 py-2 text-left text-xs font-semibold text-gray-800 border-b border-gray-200">Score</th>
                            <th className="px-3 py-2 text-left text-xs font-semibold text-gray-800 border-b border-gray-200">Description</th>
                          </tr>
                        </thead>
                        <tbody>
                          {d.sub_categories.map((sc, scIdx) => (
                            <tr key={`${sc?.name || 'sub'}-${scIdx}`} className={scIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                              <td className="px-3 py-2 text-sm text-gray-900 border-b border-gray-200 font-medium">
                                {sc?.name || 'Unnamed'}
                              </td>
                              <td className="px-3 py-2 text-sm text-gray-700 border-b border-gray-200">
                                {typeof sc?.score === 'number' ? (
                                  <span
                                    className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                      sc.score >= 8 ? 'bg-green-100 text-green-800' :
                                      sc.score >= 6 ? 'bg-yellow-100 text-yellow-800' :
                                      'bg-red-100 text-red-800'
                                    }`}
                                  >
                                    {sc.score}/10
                                  </span>
                                ) : (
                                  <span className="text-xs text-gray-500">—</span>
                                )}
                              </td>
                              <td className="px-3 py-2 text-sm text-gray-700 border-b border-gray-200">
                                {sc?.description || <span className="text-xs text-gray-500">—</span>}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {quality && (
        <div className="mb-6 p-4 bg-white rounded-lg border border-gray-200">
          <h4 className="text-md font-semibold text-gray-900 mb-2">Quality of Service</h4>
          <div className="flex flex-wrap items-center gap-3 mb-3">
            {typeof quality.overall_rating === 'number' && (
              <div className="text-sm text-gray-900">
                <span className="font-semibold">Overall rating:</span>{' '}
                <span className="text-gray-700">{quality.overall_rating}</span>
              </div>
            )}
          </div>
          {quality.summary && (
            <p className="text-sm text-gray-700 mb-4 leading-relaxed">{quality.summary}</p>
          )}

          {Array.isArray(quality.reviews) && quality.reviews.length > 0 && (
            <div className="overflow-x-auto">
              <table className="min-w-full border border-gray-200 rounded-md bg-white">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-800 border-b border-gray-200">Source</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-800 border-b border-gray-200">Rating</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-800 border-b border-gray-200">Sentiment</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-800 border-b border-gray-200">Snippet</th>
                  </tr>
                </thead>
                <tbody>
                  {quality.reviews.map((r, idx) => (
                    <tr key={`${r?.source || 'review'}-${idx}`} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="px-3 py-2 text-sm text-gray-900 border-b border-gray-200 font-medium">
                        {r?.source || '—'}
                      </td>
                      <td className="px-3 py-2 text-sm text-gray-700 border-b border-gray-200">
                        {r?.rating || <span className="text-xs text-gray-500">—</span>}
                      </td>
                      <td className="px-3 py-2 text-sm text-gray-700 border-b border-gray-200">
                        {r?.sentiment ? (
                          <span
                            className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                              String(r.sentiment).toLowerCase() === 'positive' ? 'bg-green-100 text-green-800' :
                              String(r.sentiment).toLowerCase() === 'negative' ? 'bg-red-100 text-red-800' :
                              'bg-gray-100 text-gray-800'
                            }`}
                          >
                            {r.sentiment}
                          </span>
                        ) : (
                          <span className="text-xs text-gray-500">—</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-sm text-gray-700 border-b border-gray-200">
                        {r?.snippet || <span className="text-xs text-gray-500">—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {responsiveness && (
        <div className="mb-6 p-4 bg-white rounded-lg border border-gray-200">
          <h4 className="text-md font-semibold text-gray-900 mb-2">Responsiveness</h4>
          <div className="flex flex-wrap items-center gap-3 mb-2">
            {typeof responsiveness.rating === 'number' && (
              <span
                className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                  responsiveness.rating >= 8 ? 'bg-green-100 text-green-800' :
                  responsiveness.rating >= 6 ? 'bg-yellow-100 text-yellow-800' :
                  'bg-red-100 text-red-800'
                }`}
              >
                {responsiveness.rating}/10
              </span>
            )}
            {responsiveness.summary && (
              <span className="text-sm text-gray-800 font-medium">{responsiveness.summary}</span>
            )}
          </div>
          {responsiveness.explanation && (
            <p className="text-sm text-gray-700 leading-relaxed">{responsiveness.explanation}</p>
          )}
        </div>
      )}

      {pricing && (
        <div className="mb-6 p-4 bg-white rounded-lg border border-gray-200">
          <h4 className="text-md font-semibold text-gray-900 mb-2">Pricing Level</h4>
          <div className="flex flex-wrap items-center gap-3 mb-2">
            {typeof pricing.level === 'number' && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                Level {pricing.level}
              </span>
            )}
            {pricing.summary && (
              <span className="text-sm text-gray-800 font-medium">{pricing.summary}</span>
            )}
          </div>
          {pricing.explanation && (
            <p className="text-sm text-gray-700 leading-relaxed">{pricing.explanation}</p>
          )}
        </div>
      )}
    </div>
  );
};

const ReportViewer = ({ markdownText, tenantId }) => {
  const shouldDebug =
    typeof window !== 'undefined' &&
    window?.localStorage?.getItem('debugReportViewer') === '1' &&
    process?.env?.NODE_ENV !== 'production';

  const findJsonBlock = (text) => {
    if (typeof text !== 'string' || !text.trim()) return null;

    // 1) Prefer fenced code blocks that contain a JSON object.
    // Supports:
    // ```json
    // { ... }
    // ```
    // and:
    // ```
    // { ... }
    // ```
    const fenceRe = /```[^\n]*\n([\s\S]*?)\n```/g;
    let match;
    while ((match = fenceRe.exec(text)) !== null) {
      const fullMatch = match[0];
      const inner = match[1]?.trim();
      if (!inner || !inner.startsWith('{')) continue;
      try {
        const parsed = JSON.parse(inner);
        return { json: parsed, start: match.index, end: match.index + fullMatch.length };
      } catch (_) {
        // Not valid JSON; keep searching
      }
    }

    // 2) Fallback: find a JSON object starting at a line that begins with '{'
    // and expand until JSON.parse succeeds. Works even if JSON appears mid-report.
    const lines = text.split('\n');
    for (let i = 0; i < lines.length; i++) {
      if (!lines[i] || !lines[i].trim().startsWith('{')) continue;

      let candidate = '';
      for (let j = i; j < lines.length && j < i + 5000; j++) {
        candidate += (j === i ? '' : '\n') + lines[j];
        const trimmed = candidate.trim();
        if (!trimmed.endsWith('}')) continue;

        try {
          const parsed = JSON.parse(trimmed);
          return { json: parsed, startLine: i, endLine: j };
        } catch (_) {
          // keep expanding
        }
      }
    }

    return null;
  };

  // Extract JSON data from the markdown text
  const extractJsonData = (text) => {
    try {
      const found = findJsonBlock(text);
      return found?.json ?? null;
    } catch (error) {
      console.error('Error parsing JSON:', error);
      return null;
    }
  };

  // Remove JSON from markdown text for rendering
  const cleanMarkdownText = (text) => {
    if (typeof text !== 'string') return '';

    const found = findJsonBlock(text);
    if (!found) return text.trim();

    // If fenced: remove by char range
    if (typeof found.start === 'number' && typeof found.end === 'number') {
      return (text.slice(0, found.start) + text.slice(found.end)).trim();
    }

    // If line-based: remove the JSON lines only, keeping any content after it.
    if (typeof found.startLine === 'number' && typeof found.endLine === 'number') {
      const lines = text.split('\n');
      const kept = [
        ...lines.slice(0, found.startLine),
        ...lines.slice(found.endLine + 1),
      ];
      return kept.join('\n').trim();
    }

    return text.trim();
  };

  const jsonData = extractJsonData(markdownText);
  const cleanedMarkdown = cleanMarkdownText(markdownText);
  const showBusinessAnalysis =
    BUSINESS_ANALYSIS_TENANTS.has(String(tenantId || '').trim()) &&
    jsonData &&
    jsonData.business_analysis;

  if (shouldDebug) {
    try {
      // eslint-disable-next-line no-console
      console.groupCollapsed('[ReportViewer debug]');
      // eslint-disable-next-line no-console
      console.log('tenantId:', tenantId);
      // eslint-disable-next-line no-console
      console.log('raw markdownText:', markdownText);
      // eslint-disable-next-line no-console
      console.log('cleaned markdown shown:', cleanedMarkdown);
      // eslint-disable-next-line no-console
      console.log('extracted jsonData:', jsonData);
      // eslint-disable-next-line no-console
      console.groupEnd();
    } catch (_) {
      // ignore
    }
  }

  return (
    <div className="prose prose-slate max-w-none">
      <ReactMarkdown
        children={cleanedMarkdown}
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[[rehypeSanitize, customSchema]]}
        components={{
          h1: ({ node, ...props }) => <h1 className="text-2xl font-bold mb-4" {...props} />,
          h2: ({ node, ...props }) => <h2 className="text-xl font-bold mb-3" {...props} />,
          h3: ({ node, ...props }) => <h3 className="text-lg font-bold mb-2" {...props} />,
          p: ({ node, ...props }) => <p className="mb-4 leading-relaxed" {...props} />,
          ul: ({ node, ...props }) => <ul className="list-disc pl-6 mb-4 space-y-2" {...props} />,
          ol: ({ node, ...props }) => <ol className="list-decimal pl-6 mb-4 space-y-2" {...props} />,
          li: ({ node, ...props }) => <li className="mb-1" {...props} />,
          strong: ({ node, ...props }) => <strong className="font-bold" {...props} />,
          em: ({ node, ...props }) => <em className="italic" {...props} />,
          table: ({ node, ...props }) => (
            <div className="my-5 w-full overflow-x-auto rounded-lg border border-slate-200">
              <table className="min-w-[980px] w-full border-collapse text-sm" {...props} />
            </div>
          ),
          th: ({ node, ...props }) => <th className="whitespace-nowrap bg-slate-50 px-3 py-3 text-left text-xs font-semibold text-slate-700" {...props} />,
          td: ({ node, ...props }) => <td className="border-t border-slate-100 px-3 py-3 align-top text-slate-700" {...props} />,
          a: ({ node, ...props }) => <a className="font-medium text-blue-700 underline underline-offset-2 hover:text-blue-900" target="_blank" rel="noreferrer" {...props} />,
        }}
      />

      {showBusinessAnalysis && (
        <BusinessAnalysisSection businessAnalysis={jsonData.business_analysis} />
      )}
      
      {/* Display risk scores table if JSON data is available */}
      {jsonData && jsonData.individual_risk_scores && (
        <div className="mt-8 not-prose">
          <h3 className="text-lg font-bold mb-4">Individual Risk Scores</h3>
          <RiskScoresTable riskScores={jsonData.individual_risk_scores} />
          
          {/* Display overall risk information */}
          {(jsonData.overall_financial_risk_level || jsonData.overall_financial_risk_score_numeric) && (
            <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <h4 className="text-md font-semibold text-blue-900 mb-2">Overall Risk Assessment</h4>
              <div className="flex flex-wrap gap-4">
                {jsonData.overall_financial_risk_level && (
                  <div>
                    <span className="text-sm font-medium text-blue-700">Risk Level: </span>
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                      jsonData.overall_financial_risk_level.toLowerCase() === 'high' ? 'bg-red-100 text-red-800' :
                      jsonData.overall_financial_risk_level.toLowerCase() === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-green-100 text-green-800'
                    }`}>
                      {jsonData.overall_financial_risk_level}
                    </span>
                  </div>
                )}
                {jsonData.overall_financial_risk_score_numeric && (
                  <div>
                    <span className="text-sm font-medium text-blue-700">Pulse Index: </span>
                    <span className="text-sm font-semibold text-blue-900">
                      {jsonData.overall_financial_risk_score_numeric}/10
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ReportViewer;
